import type {
  BaseKey,
  BaseRecord,
  CrudFilter,
  CrudSort,
  DataProvider,
  HttpError,
  Pagination,
} from '@refinedev/core'
import type { Filter, ListQuery, ListResult } from '@sirius/contracts/admin'
import {
  filterOperatorSchema,
  MAX_PAGE_SIZE,
  PLATFORM_SETTINGS_ID,
} from '@sirius/contracts/admin'
import type { AdminClient } from '~/api/adminClient'
import { toHttpError } from './errors'

/** Refine's `useTable` defaults. */
const DEFAULT_PAGE_SIZE = 10

/**
 * The admin procedures one Refine resource maps onto; a missing one fails
 * with 405 (read-only, list-only or single-record resources). Method syntax
 * on purpose: the variables Refine passes are untyped (`{}`), the contract's
 * zod schemas validate them on the server.
 */
interface ResourceProcedures {
  list?(input: ListQuery): Promise<ListResult<BaseRecord>>
  get?(input: { id: string }): Promise<BaseRecord>
  create?(input: object): Promise<BaseRecord>
  update?(input: object): Promise<BaseRecord>
  delete?(input: { id: string }): Promise<BaseRecord>
}

const UNSUPPORTED: Record<keyof ResourceProcedures, string> = {
  list: 'listed',
  get: 'read one by one',
  create: 'created',
  update: 'updated',
  delete: 'deleted',
}

/** The procedure, or a 405 when the resource does not support it. */
function procedure<Name extends keyof typeof UNSUPPORTED>(
  procedures: ResourceProcedures,
  name: Name,
  resource: string,
): NonNullable<ResourceProcedures[Name]> {
  const found = procedures[name]
  if (found === undefined) {
    throw httpError(405, `${resource} cannot be ${UNSUPPORTED[name]}`)
  }
  return found
}

/**
 * The activity log (`audit.list`): read as a list only. Scoped to the host's
 * tenant like the tenant-owned resources, for staff too: another tenant's
 * log is on that tenant's host.
 */
export const ACTIVITY_RESOURCE = 'activity'

/**
 * One record's history (the show pages' History): `audit.list` like
 * `activity`, but scoped to the record's tenant, which the caller passes as a
 * `tenantId` filter. A tenant's page on another tenant's host (platform pages,
 * staff) shows that tenant's history. Not in `resources.tsx`, so no URL picks
 * it: `/activity` stays the host tenant's log.
 */
export const RECORD_ACTIVITY_RESOURCE = 'recordActivity'

/**
 * The platform's activity log (`audit.list` too, staff only): every tenant's
 * events and the platform events without a tenant. Not scoped to the host.
 */
export const PLATFORM_ACTIVITY_RESOURCE = 'platformActivity'

/** The activity log's actor filter: staff only. */
export const ACTIVITY_ACTOR_FIELD = 'actorUserId'

/** The activity log's filters only staff may use: by actor, a user's history. */
const STAFF_ONLY_ACTIVITY_FIELDS = new Set([ACTIVITY_ACTOR_FIELD, 'userId'])

/** Resources read from `audit.list`. */
const AUDIT_RESOURCES = [
  ACTIVITY_RESOURCE,
  RECORD_ACTIVITY_RESOURCE,
  PLATFORM_ACTIVITY_RESOURCE,
] as const

function isAudit(resource: string) {
  return (AUDIT_RESOURCES as readonly string[]).includes(resource)
}

/** Whether the filters name the tenant to scope to (`tenantId eq <id>`). */
function hasTenantFilter(filters: CrudFilter[] | undefined) {
  return (filters ?? []).some(
    (filter) =>
      'field' in filter &&
      filter.field === 'tenantId' &&
      filter.operator === 'eq' &&
      typeof filter.value === 'string' &&
      filter.value !== '',
  )
}

/**
 * A cabinet's rounds (`rounds.list` / `rounds.get`), read-only: for staff and
 * the host tenant's owners/admins (the API answers FORBIDDEN to members).
 */
export const ROUNDS_RESOURCE = 'rounds'

/**
 * A game's configurations as the cabinet form's configuration picker lists
 * them, list only: the platform's and the host tenant's own (`availableTo`),
 * never another tenant's, staff included. For staff and the tenant's
 * owners/admins (`GAME_CONFIGURATION_ROLES`; the API answers FORBIDDEN to
 * members). Not in `resources.tsx`: no route. Staff read every configuration
 * under Platform › Games (`gameConfigurations`).
 */
export const TENANT_CONFIGURATIONS_RESOURCE = 'tenantConfigurations'

/**
 * The platform's settings (`platformSettings.get` / `update`, staff only):
 * one record, Refine id `PLATFORM_SETTINGS_ID`, never listed.
 */
export const PLATFORM_SETTINGS_RESOURCE = 'platformSettings'

/** Resources whose rows belong to a tenant: scoped to the host's tenant. */
export const TENANT_OWNED_RESOURCES = [
  'locations',
  'cabinets',
  ACTIVITY_RESOURCE,
  ROUNDS_RESOURCE,
] as const

function isTenantOwned(resource: string) {
  return (TENANT_OWNED_RESOURCES as readonly string[]).includes(resource)
}

function httpError(statusCode: number, message: string): HttpError {
  return { statusCode, message }
}

/**
 * The admin API lets staff and multi-tenant users read any tenant's rows; on
 * a tenant host another tenant's record is not found (spec §3), so a
 * `nord.<base>/cabinets/<alpen id>` URL shows the not-found view.
 */
function assertOwnedBy(
  resource: string,
  tenantId: string | undefined,
  record: BaseRecord,
) {
  if (tenantId !== undefined && record.tenantId !== tenantId) {
    throw httpError(404, `No ${resource} record with id "${String(record.id)}"`)
  }
}

/** The platform's settings as a Refine record. */
function withSettingsId(settings: object): BaseRecord {
  return { id: PLATFORM_SETTINGS_ID, ...settings }
}

function resourceProcedures(
  client: AdminClient,
  resource: string,
): ResourceProcedures {
  // Picked one by one: the oRPC client is a proxy that returns a procedure for
  // any key, so `client.tenants.delete` would exist and fail on the server.
  switch (resource) {
    case 'tenants': {
      const { list, get, create, update } = client.tenants
      return { list, get, create, update }
    }
    case 'locations': {
      const { list, get, create, update } = client.locations
      return { list, get, create, update, delete: client.locations.delete }
    }
    case 'cabinets': {
      // cabinets are decommissioned (`setStatus`), never deleted
      const { list, get, create, update } = client.cabinets
      return { list, get, create, update }
    }
    case 'users': {
      // every account, staff only (the API answers FORBIDDEN otherwise).
      // Read-only: Better Auth's admin API changes a user
      const { list, get } = client.users
      return { list, get }
    }
    case 'games': {
      // staff only (the API answers FORBIDDEN otherwise). A game comes with
      // a release; staff change only its configurations (below)
      const { list, get } = client.games
      return { list, get }
    }
    case 'gameConfigurations': {
      // a game's configurations, every one, staff only (Platform › Games).
      // The configuration pages create, copy, save, publish and discard
      // (`games.configurations.create` / `saveDraft` / `publish` /
      // `discardDraft`) and pick the default (`games.setDefaultConfiguration`)
      // through the client
      const { list, get } = client.games.configurations
      return { list, get }
    }
    case TENANT_CONFIGURATIONS_RESOURCE:
      // what the host tenant may run, for the cabinet form's picker (scoped
      // below; tenant users get no staff author)
      return { list: client.games.configurations.list }
    case 'gameOptions':
      // every game a cabinet can offer, for any signed-in user: the options
      // of the cabinet form and the names of the cabinet list's games column
      return { list: client.cabinets.gameOptions }
    case ROUNDS_RESOURCE: {
      // written by the game server as a cabinet plays: read-only here
      const { list, get } = client.rounds
      return { list, get }
    }
    case ACTIVITY_RESOURCE:
    case RECORD_ACTIVITY_RESOURCE:
    case PLATFORM_ACTIVITY_RESOURCE:
      // append-only: no get, create, update or delete
      return { list: client.audit.list }
    case PLATFORM_SETTINGS_RESOURCE:
      // one record without an id of its own: Refine's id is
      // PLATFORM_SETTINGS_ID, which the procedures do not take
      return {
        get: async () => withSettingsId(await client.platformSettings.get()),
        update: async ({ id: _id, ...settings }: { id?: string }) =>
          withSettingsId(await client.platformSettings.update(settings)),
      }
    default:
      throw httpError(404, `Unknown resource "${resource}"`)
  }
}

function toFilters(filters: CrudFilter[] | undefined): Filter[] | undefined {
  return filters?.map((filter) => {
    if (!('field' in filter)) {
      throw httpError(
        400,
        `"${filter.operator}" filters are not supported, the admin API ANDs all filters`,
      )
    }

    const operator = filterOperatorSchema.safeParse(filter.operator)
    if (!operator.success) {
      throw httpError(400, `Unsupported filter operator "${filter.operator}"`)
    }

    return { field: filter.field, operator: operator.data, value: filter.value }
  })
}

function toSort(sorters: CrudSort[] | undefined): ListQuery['sort'] {
  return sorters?.map(({ field, order }) => ({ field, order }))
}

/** Refine ids are `string | number`, the admin API uses uuids. */
function toId(id: BaseKey) {
  return String(id)
}

/** Rethrows oRPC errors as Refine `HttpError`s (status + message). */
async function call<Result>(request: () => Promise<Result>): Promise<Result> {
  try {
    return await request()
  } catch (error) {
    throw toHttpError(error)
  }
}

type ListProcedure = NonNullable<ResourceProcedures['list']>

async function listAll(
  list: ListProcedure,
  query: Omit<ListQuery, 'page' | 'pageSize'>,
) {
  const items: BaseRecord[] = []
  let total = 0
  let page = 1

  do {
    const result = await list({
      page,
      pageSize: MAX_PAGE_SIZE,
      ...query,
    })
    items.push(...result.items)
    total = result.total
    page += 1

    if (result.items.length === 0) {
      break
    }
  } while (items.length < total)

  return { items, total }
}

function fetchList(
  list: ListProcedure,
  pagination: Pagination | undefined,
  query: Omit<ListQuery, 'page' | 'pageSize'>,
) {
  const mode = pagination?.mode ?? 'server'

  // client-side and no pagination: Refine expects every record
  if (mode !== 'server') {
    return listAll(list, query)
  }

  return list({
    page: pagination?.currentPage ?? 1,
    pageSize: pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
    ...query,
  })
}

/**
 * Refine data provider for the admin API: resources `tenants`, `locations`
 * and `cabinets` map onto the contract's list/get/create/update procedures
 * (`delete` for locations), `users`, `games`, `gameConfigurations` (a
 * game's configurations, `games.configurations`) and `rounds` (a cabinet's
 * rounds) onto list/get, `tenantConfigurations` (what the host tenant may
 * run) onto `games.configurations.list`,
 * `gameOptions` onto `cabinets.gameOptions`, `activity`, `recordActivity` and `platformActivity`
 * onto `audit.list` (all list only), `platformSettings` onto
 * `platformSettings.get` / `update` (one record). `getList` maps 1:1 onto `ListQuery` (see
 * `packages/contracts/README.md`). Refine's generics are erased to
 * `BaseRecord`; screens pass the contract types (`Tenant`, ...) to the hooks.
 */
export function createDataProvider({
  client,
  apiUrl,
  getTenantId,
  isPlatformAdmin,
}: {
  client: AdminClient
  apiUrl: string
  getTenantId: () => string | undefined
  /** platform admins: only they may filter the activity log by actor */
  isPlatformAdmin: () => Promise<boolean>
}): DataProvider {
  const hostTenantId = (resource: string) => {
    const tenantId = getTenantId()
    if (tenantId === undefined) {
      throw httpError(400, `No active tenant for ${resource}`)
    }
    return tenantId
  }
  const tenantIdFor = (resource: string) =>
    isTenantOwned(resource) ? hostTenantId(resource) : undefined
  /**
   * The host's tenant for the configurations its cabinets may run (the
   * platform's and its own), else undefined.
   */
  const availableToFor = (resource: string) =>
    resource === TENANT_CONFIGURATIONS_RESOURCE
      ? hostTenantId(resource)
      : undefined

  /**
   * Whether the user may filter the activity log by actor or user: the API
   * refuses those filters for tenant users, but a URL a staff user shared may
   * carry them.
   */
  const mayFilterByUser = async (resource: string) =>
    !isAudit(resource) || (await isPlatformAdmin())

  return {
    getApiUrl: () => apiUrl,

    getList: async ({ resource, pagination, sorters, filters }) => {
      const list = procedure(
        resourceProcedures(client, resource),
        'list',
        resource,
      )
      if (resource === RECORD_ACTIVITY_RESOURCE && !hasTenantFilter(filters)) {
        throw httpError(400, "A record's history needs the record's tenant")
      }
      const tenantId = tenantIdFor(resource)
      const availableTo = availableToFor(resource)
      const userFilters = await mayFilterByUser(resource)
      const scoped = [
        ...(tenantId === undefined
          ? []
          : [{ field: 'tenantId', operator: 'eq' as const, value: tenantId }]),
        ...(availableTo === undefined
          ? []
          : [
              {
                field: 'availableTo',
                operator: 'eq' as const,
                value: availableTo,
              },
            ]),
        ...(toFilters(filters) ?? []).filter(
          ({ field }) => userFilters || !STAFF_ONLY_ACTIVITY_FIELDS.has(field),
        ),
      ]
      const query = {
        sort: toSort(sorters),
        filters: scoped.length > 0 ? scoped : undefined,
      }
      const { items, total } = await call(() =>
        fetchList(list, pagination, query),
      )

      return { data: items as never[], total }
    },

    getOne: async ({ resource, id }) => {
      const procedures = resourceProcedures(client, resource)
      const tenantId = tenantIdFor(resource)
      const get = procedure(procedures, 'get', resource)
      const data = await call(() => get({ id: toId(id) }))
      assertOwnedBy(resource, tenantId, data)

      return { data: data as never }
    },

    getMany: async ({ resource, ids }) => {
      const procedures = resourceProcedures(client, resource)
      const tenantId = tenantIdFor(resource)
      const get = procedure(procedures, 'get', resource)
      const data = await call(() =>
        Promise.all(ids.map((id) => get({ id: toId(id) }))),
      )
      for (const record of data) {
        assertOwnedBy(resource, tenantId, record)
      }

      return { data: data as never[] }
    },

    create: async ({ resource, variables }) => {
      const procedures = resourceProcedures(client, resource)
      const create = procedure(procedures, 'create', resource)
      const tenantId = tenantIdFor(resource)
      const data = await call(() =>
        create({
          ...(variables as object),
          ...(tenantId === undefined ? {} : { tenantId }),
        }),
      )

      return { data: data as never }
    },

    update: async ({ resource, id, variables }) => {
      const procedures = resourceProcedures(client, resource)
      const update = procedure(procedures, 'update', resource)
      const data = await call(() =>
        update({ ...(variables as object), id: toId(id) }),
      )

      return { data: data as never }
    },

    deleteOne: async ({ resource, id }) => {
      const procedures = resourceProcedures(client, resource)
      const remove = procedure(procedures, 'delete', resource)
      const data = await call(() => remove({ id: toId(id) }))

      return { data: data as never }
    },
  }
}
